import type { DeckingItem, MaterialsNodeData } from '../types';

/** Loose decking source: any object carrying some subset of the decking fields. */
export type DeckingSource = Partial<
  Pick<
    MaterialsNodeData,
    | 'deckingItems'
    | 'deckingSqFt'
    | 'deckingWidthInches'
    | 'deckingPricePerLinFt'
    | 'deckingProductName'
    | 'deckingTotal'
  >
>;

/**
 * Standard S4S dressed width for a nominal board width (inches):
 * nominal < 8 subtracts 1/2", nominal >= 8 subtracts 3/4".
 * (6 -> 5.5, 8 -> 7.25, 10 -> 9.25, 12 -> 11.25, 4 -> 3.5)
 */
export function dressedDeckingWidth(nominal: number): number {
  if (!Number.isFinite(nominal) || nominal <= 0) return 0;
  return nominal < 8 ? nominal - 0.5 : nominal - 0.75;
}

/**
 * Parse the nominal board width from a product name like "3x8 KD DF T&G"
 * (the second dimension). Returns null when no size is present.
 */
export function parseNominalDeckingWidth(name: string | undefined | null): number | null {
  if (!name) return null;
  const match = name.match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i);
  if (!match) return null;
  const nominal = parseFloat(match[2]);
  return Number.isFinite(nominal) && nominal > 0 ? nominal : null;
}

/** Dressed board width derived from the nominal size in a product name, or null. */
export function deckingWidthFromName(name: string | undefined | null): number | null {
  const nominal = parseNominalDeckingWidth(name);
  return nominal == null ? null : dressedDeckingWidth(nominal);
}

/**
 * Board width to use for a decking line. The nominal size in the product name is
 * authoritative (fixes stale/mis-entered widths like a "3x8" saved as 5.5"),
 * falling back to the stored width when the name has no parseable size.
 */
export function resolveDeckingWidthInches(
  productName: string | undefined | null,
  storedWidth: number | undefined | null,
): number {
  const fromName = deckingWidthFromName(productName);
  if (fromName != null && fromName > 0) return fromName;
  return storedWidth && storedWidth > 0 ? storedWidth : 0;
}

/** Square feet to linear feet: round((sqFt × 12) / widthInches). 0 when inputs non-positive. */
export function deckingLinearFeet(sqFt: number, widthInches: number): number {
  if (widthInches <= 0 || sqFt <= 0) return 0;
  return Math.round((sqFt * 12) / widthInches);
}

/**
 * Extended total for one decking item using the item's own width. Callers that
 * want stale/legacy widths corrected should pass items through
 * {@link getStructureDeckingItems} first (it resolves widths), which lets the
 * editors preserve deliberate manual width overrides.
 */
export function deckingItemTotal(item: Pick<DeckingItem, 'sqFt' | 'pricePerLinFt' | 'widthInches'>): number {
  const linft = deckingLinearFeet(item.sqFt || 0, item.widthInches || 0);
  return Math.round(linft * (item.pricePerLinFt || 0));
}

function legacyDeckingItem(materials: DeckingSource): DeckingItem | null {
  const sqFt = materials.deckingSqFt || 0;
  const hasContent = sqFt > 0 || !!materials.deckingProductName?.trim();
  if (!hasContent) return null;
  const widthInches = resolveDeckingWidthInches(materials.deckingProductName, materials.deckingWidthInches);
  return {
    id: 'legacy-decking',
    productName: materials.deckingProductName,
    widthInches,
    sqFt,
    pricePerLinFt: materials.deckingPricePerLinFt || 0,
    total: materials.deckingTotal || deckingItemTotal({
      sqFt,
      widthInches,
      pricePerLinFt: materials.deckingPricePerLinFt || 0,
    }),
  };
}

/**
 * Unified decking items for a structure. Returns `deckingItems` when present,
 * otherwise synthesizes a single item from the legacy single-decking fields.
 * Widths are resolved (nominal-name derivation) so old proposals display correctly.
 */
export function getStructureDeckingItems(materials: DeckingSource): DeckingItem[] {
  const items = materials.deckingItems;
  if (Array.isArray(items) && items.length > 0) {
    return items
      .filter(item => (item.sqFt || 0) > 0 || !!item.productName?.trim())
      .map(item => ({
        ...item,
        widthInches: resolveDeckingWidthInches(item.productName, item.widthInches),
      }));
  }
  const legacy = legacyDeckingItem(materials);
  return legacy ? [legacy] : [];
}

/** Sum of decking item totals for a structure. */
export function structureDeckingTotal(materials: DeckingSource): number {
  return getStructureDeckingItems(materials).reduce(
    (sum, item) => sum + deckingItemTotal(item),
    0,
  );
}

export function structureHasIncludedDecking(materials: DeckingSource): boolean {
  return getStructureDeckingItems(materials).some(
    item => (item.sqFt || 0) > 0 && !!item.productName?.trim(),
  );
}

export function structureDeckingIncludedLabel(materials: DeckingSource): string {
  const names = getStructureDeckingItems(materials)
    .map(item => item.productName?.trim())
    .filter((name): name is string => !!name);
  const unique = Array.from(new Set(names));
  return unique.length ? `Roof decking included — ${unique.join(', ')}` : 'Roof decking included';
}
